// ---------------------------------------------------------------------------
// WorkspaceDrawerContext — open/close state for the workspace management
// drawer. The drawer is a meta-level overlay (list, create, settings) that
// sits above page content rather than being a separate route, so the user
// never loses their current working context.
// ---------------------------------------------------------------------------

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';

// Discriminated union — closed is represented by `view === null`.
export type WorkspaceDrawerView =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'settings'; name: string };

interface WorkspaceDrawerContextValue {
  view: WorkspaceDrawerView | null;
  open: (view: WorkspaceDrawerView) => void;
  close: () => void;
}

const WorkspaceDrawerContext =
  createContext<WorkspaceDrawerContextValue | null>(null);

export function WorkspaceDrawerProvider({ children }: PropsWithChildren) {
  const [view, setView] = useState<WorkspaceDrawerView | null>(null);

  const open = useCallback((next: WorkspaceDrawerView) => {
    setView(next);
  }, []);

  const close = useCallback(() => {
    setView(null);
  }, []);

  const value = useMemo<WorkspaceDrawerContextValue>(
    () => ({ view, open, close }),
    [view, open, close],
  );

  return (
    <WorkspaceDrawerContext.Provider value={value}>
      {children}
    </WorkspaceDrawerContext.Provider>
  );
}

export function useWorkspaceDrawer(): WorkspaceDrawerContextValue {
  const ctx = useContext(WorkspaceDrawerContext);
  if (!ctx) {
    throw new Error(
      'useWorkspaceDrawer must be used within <WorkspaceDrawerProvider>',
    );
  }
  return ctx;
}
